import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Account.css';

interface Subscription {
  customerId?: string;
  email?: string;
  subscriptionId?: string;
  status?: string;
  createdAt?: string;
  currentPeriodStart?: number | string | null;
  currentPeriodEnd?: number | string | null;
  metadata?: Record<string, any>;
  priceId?: string;
  cancelAtPeriodEnd?: boolean;
  cancelledAt?: string;
  trialEnd?: string | number | null;
  customer?: {
    name?: string;
    email?: string;
    phone?: string;
    address?: {
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      postal_code?: string;
      country?: string;
    };
  };
}

interface ProfileData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  dietaryRestrictions?: string;
  spicePreference?: string;
  cuisinePreferences?: string[];
  foodDislikes?: string[];
  notifications?: {
    mealReminders?: boolean;
    expiringItems?: boolean;
    weeklyPlanning?: boolean;
    productUpdates?: boolean;
  };
}

const Account: React.FC = () => {
  const { user, getIdToken } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [profileData, setProfileData] = useState<ProfileData>({
    notifications: {
      mealReminders: true,
      expiringItems: true,
      weeklyPlanning: false,
      productUpdates: false,
    }
  });
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'subscription' | 'preferences'>('profile');

  // Configure API URLs
  const subscriptionApiBase = 
    process.env.REACT_APP_SUBSCRIPTION_API || 
    process.env.REACT_APP_API_URL || 
    'https://5ucocav1p0.execute-api.us-east-2.amazonaws.com/prod/subscription';
  
  const portalApiBase = subscriptionApiBase.replace(/\/subscription\/?$/, '');

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user?.email) return;
      setLoading(true);
      setError(null);
      
      try {
        const url = `${subscriptionApiBase}?email=${encodeURIComponent(user.email)}`;
        const token = await (getIdToken ? getIdToken() : Promise.resolve(null));
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(url, {
          method: 'GET',
          headers,
        });

        if (res.ok) {
          const data = await res.json();
          setSubscription(data);
          
          // Extract customer data from subscription
          if (data.customer) {
            const names = data.customer.name?.split(' ') || [];
            setProfileData(prev => ({
              ...prev,
              firstName: names[0] || '',
              lastName: names.slice(1).join(' ') || '',
              phone: data.customer.phone || '',
            }));
          }
        } else if (res.status === 404) {
          setSubscription(null);
        } else {
          const errorText = await res.text();
          console.error('Subscription fetch failed:', res.status, errorText);
          setError('Failed to load subscription');
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setError('Failed to load subscription');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user?.email, subscriptionApiBase, getIdToken]);

  const openPortal = async () => {
    if (!subscription?.customerId) {
      setError('No customer ID available to open billing portal.');
      return;
    }
    
    setPortalLoading(true);
    setError(null);
    
    try {
      const token = await (getIdToken ? getIdToken() : Promise.resolve(null));
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const resp = await fetch(`${portalApiBase}/portal`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ customerId: subscription.customerId }),
      });

      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to create portal session');
      }

      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url;
        return;
      }

      throw new Error('No portal URL returned');
    } catch (err: any) {
      console.error('Portal error:', err);
      setError(err.message || 'Unable to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async (priceId = 'price_1RumRuKpXGPARJXNpqfYPTFq') => {
    if (!user) {
      window.location.href = '/login';
      return;
    }

    const apiRoot = portalApiBase;
    const url = apiRoot.endsWith('/') ? apiRoot + 'checkout' : `${apiRoot}/checkout`;

    try {
      const body = {
        priceId,
        email: user.email,
        userId: user.userId,
        metadata: {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
        }
      };
      
      const token = await (getIdToken ? getIdToken() : Promise.resolve(null));
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });

      if (!res.ok) {
        throw new Error('Checkout creation failed');
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        window.location.href = '/pricing';
      }
    } catch (e) {
      console.error('Checkout error', e);
      window.location.href = '/pricing';
    }
  };

  const saveProfile = async () => {
    setSaveMessage('Profile saved successfully!');
    setTimeout(() => setSaveMessage(null), 3000);
  };

  const formatDate = (val?: string | number | null) => {
    if (!val || val === null) return 'Not set';
    
    try {
      const ts = typeof val === 'number' ? val : Number(val);
      let date: Date;
      if (!isNaN(ts)) {
        date = new Date(ts < 1e10 ? ts * 1000 : ts);
      } else {
        date = new Date(String(val));
      }
      
      if (isNaN(date.getTime())) {
        return 'Not set';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Not set';
    }
  };

  // Map price IDs to plan names
  const getPlanDetails = (priceId?: string, status?: string) => {
    const planMap: Record<string, { name: string; price: string; description: string; features: string[] }> = {
      'price_1RumRuKpXGPARJXNpqfYPTFq': {
        name: 'Household Plan',
        price: '$25/month',
        description: 'Perfect for families with shared meal planning',
        features: [
          'Unlimited meal plans',
          'Smart grocery lists',
          'Nutrition tracking',
          'Household sharing',
          'Recipe recommendations',
          'Priority support'
        ]
      },
      'price_1RumTGKpXGPARJXNGlJMRdgH': {
        name: 'Premium Plan',
        price: '$49/month',
        description: 'Unlimited meal plans with advanced features',
        features: [
          'Everything in Household',
          'Advanced AI features',
          'Custom meal plans',
          'Dietary coaching',
          'API access',
          'White-glove support'
        ]
      },
    };
    
    const plan = planMap[priceId || ''] || { 
      name: 'Free Plan', 
      price: 'Free',
      description: 'Get started with basic features',
      features: [
        '1 meal plan per month',
        'Basic recipes',
        'Simple grocery lists',
        'Community support'
      ]
    };
    
    if (status === 'trialing') {
      return {
        ...plan,
        price: `${plan.price} (Free Trial)`
      };
    }
    
    return plan;
  };

  const renderProfileTab = () => (
    <div className="tab-content">
      <div className="profile-section">
        <h3 className="section-title">Personal Information</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Email Address</label>
            <input 
              type="email" 
              value={subscription?.email || user?.email || ''} 
              disabled 
              className="input-field disabled"
            />
          </div>
          <div className="form-group">
            <label>Customer ID</label>
            <input 
              type="text" 
              value={subscription?.customerId || 'Not available'} 
              disabled 
              className="input-field disabled"
            />
          </div>
          <div className="form-group">
            <label>First Name</label>
            <input 
              type="text" 
              value={profileData.firstName || ''} 
              onChange={(e) => setProfileData({...profileData, firstName: e.target.value})}
              placeholder="Enter your first name"
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input 
              type="text" 
              value={profileData.lastName || ''} 
              onChange={(e) => setProfileData({...profileData, lastName: e.target.value})}
              placeholder="Enter your last name"
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input 
              type="tel" 
              value={profileData.phone || ''} 
              onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
              placeholder="(555) 123-4567"
              className="input-field"
            />
          </div>
        </div>
      </div>

      <div className="profile-section">
        <h3 className="section-title">Notification Preferences</h3>
        <div className="checkbox-group">
          <label className="checkbox-label">
            <input 
              type="checkbox" 
              checked={profileData.notifications?.mealReminders}
              onChange={(e) => setProfileData({
                ...profileData, 
                notifications: {...profileData.notifications, mealReminders: e.target.checked}
              })}
            />
            <span>Email notifications for meal reminders</span>
          </label>
          <label className="checkbox-label">
            <input 
              type="checkbox" 
              checked={profileData.notifications?.expiringItems}
              onChange={(e) => setProfileData({
                ...profileData, 
                notifications: {...profileData.notifications, expiringItems: e.target.checked}
              })}
            />
            <span>Alerts for expiring pantry items</span>
          </label>
          <label className="checkbox-label">
            <input 
              type="checkbox" 
              checked={profileData.notifications?.weeklyPlanning}
              onChange={(e) => setProfileData({
                ...profileData, 
                notifications: {...profileData.notifications, weeklyPlanning: e.target.checked}
              })}
            />
            <span>Weekly meal planning suggestions</span>
          </label>
          <label className="checkbox-label">
            <input 
              type="checkbox" 
              checked={profileData.notifications?.productUpdates}
              onChange={(e) => setProfileData({
                ...profileData, 
                notifications: {...profileData.notifications, productUpdates: e.target.checked}
              })}
            />
            <span>Product updates and new features</span>
          </label>
        </div>
      </div>

      <div className="action-bar">
        {saveMessage && (
          <div className="save-message">✅ {saveMessage}</div>
        )}
        <button className="button-primary" onClick={saveProfile}>
          Save Changes
        </button>
      </div>
    </div>
  );

  const renderSubscriptionTab = () => {
    if (loading) {
      return <div className="loading-state">⏳ Loading subscription details...</div>;
    }

    if (error) {
      return <div className="error-state">⚠️ {error}</div>;
    }

    const planDetails = getPlanDetails(subscription?.priceId, subscription?.status);
    const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trialing';

    return (
      <div className="tab-content">
        <div className="subscription-overview">
          <div className="plan-card current-plan">
            <div className="plan-header">
              <h3 className="plan-name">{planDetails.name}</h3>
              {hasActiveSubscription && (
                <span className={`plan-badge ${subscription?.status}`}>
                  {subscription?.status === 'trialing' ? '🎉 Trial' : '✓ Active'}
                </span>
              )}
            </div>
            <div className="plan-price">{planDetails.price}</div>
            <p className="plan-description">{planDetails.description}</p>
            
            <ul className="feature-list">
              {planDetails.features.map((feature, index) => (
                <li key={index} className="feature-item">
                  <span className="feature-icon">✓</span>
                  {feature}
                </li>
              ))}
            </ul>

            {hasActiveSubscription ? (
              <div className="subscription-details">
                <div className="detail-row">
                  <span className="detail-label">Current Period:</span>
                  <span className="detail-value">
                    {formatDate(subscription?.currentPeriodStart)} - {formatDate(subscription?.currentPeriodEnd)}
                  </span>
                </div>
                {subscription?.status === 'trialing' && subscription?.trialEnd && (
                  <div className="detail-row">
                    <span className="detail-label">Trial Ends:</span>
                    <span className="detail-value">{formatDate(subscription.trialEnd)}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Next Billing:</span>
                  <span className="detail-value">
                    {subscription?.cancelAtPeriodEnd 
                      ? 'Subscription ending' 
                      : formatDate(subscription?.currentPeriodEnd)}
                  </span>
                </div>
              </div>
            ) : null}

            <div className="plan-actions">
              {hasActiveSubscription ? (
                <>
                  <button 
                    className="button-secondary"
                    onClick={openPortal}
                    disabled={portalLoading}
                  >
                    {portalLoading ? '⏳ Loading...' : '⚙️ Manage Billing'}
                  </button>
                  {subscription?.priceId !== 'price_1RumTGKpXGPARJXNGlJMRdgH' && (
                    <button 
                      className="button-upgrade"
                      onClick={() => handleUpgrade('price_1RumTGKpXGPARJXNGlJMRdgH')}
                    >
                      ⚡ Upgrade to Premium
                    </button>
                  )}
                </>
              ) : (
                <button 
                  className="button-primary"
                  onClick={() => handleUpgrade()}
                >
                  🚀 Start Free Trial
                </button>
              )}
            </div>
          </div>

          {!hasActiveSubscription && (
            <div className="upgrade-prompt">
              <h3 className="upgrade-title">🎯 Unlock Your Full Potential</h3>
              <p className="upgrade-description">
                Join thousands of families saving time and eating healthier with NutriRAI Premium.
              </p>
              <div className="plan-comparison">
                <div className="plan-card compact">
                  <h4>Household Plan</h4>
                  <div className="plan-price">$25/mo</div>
                  <button 
                    className="button-secondary"
                    onClick={() => handleUpgrade('price_1RumRuKpXGPARJXNpqfYPTFq')}
                  >
                    Choose Household
                  </button>
                </div>
                <div className="plan-card compact featured">
                  <div className="featured-badge">Most Popular</div>
                  <h4>Premium Plan</h4>
                  <div className="plan-price">$49/mo</div>
                  <button 
                    className="button-primary"
                    onClick={() => handleUpgrade('price_1RumTGKpXGPARJXNGlJMRdgH')}
                  >
                    Choose Premium
                  </button>
                </div>
              </div>
            </div>
          )}

          {subscription?.cancelAtPeriodEnd && (
            <div className="cancellation-notice">
              <strong>⚠️ Subscription Ending</strong>
              <p>Your subscription will end on {formatDate(subscription.currentPeriodEnd)}. 
              You can reactivate anytime before this date through the billing portal.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPreferencesTab = () => (
    <div className="tab-content">
      <div className="profile-section">
        <h3 className="section-title">Dietary Preferences</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Dietary Restrictions</label>
            <select 
              value={profileData.dietaryRestrictions || 'none'}
              onChange={(e) => setProfileData({...profileData, dietaryRestrictions: e.target.value})}
              className="input-field"
            >
              <option value="none">None</option>
              <option value="vegetarian">Vegetarian</option>
              <option value="vegan">Vegan</option>
              <option value="pescatarian">Pescatarian</option>
              <option value="gluten-free">Gluten-Free</option>
              <option value="dairy-free">Dairy-Free</option>
              <option value="keto">Keto</option>
              <option value="paleo">Paleo</option>
              <option value="halal">Halal</option>
              <option value="kosher">Kosher</option>
            </select>
          </div>
          <div className="form-group">
            <label>Spice Preference</label>
            <select 
              value={profileData.spicePreference || 'medium'}
              onChange={(e) => setProfileData({...profileData, spicePreference: e.target.value})}
              className="input-field"
            >
              <option value="mild">Mild</option>
              <option value="medium">Medium</option>
              <option value="hot">Hot</option>
              <option value="extra-hot">Extra Hot</option>
            </select>
          </div>
        </div>
        
        <div className="form-group">
          <label>Favorite Cuisines</label>
          <input 
            type="text" 
            value={profileData.cuisinePreferences?.join(', ') || ''}
            onChange={(e) => setProfileData({
              ...profileData, 
              cuisinePreferences: e.target.value.split(',').map(s => s.trim())
            })}
            placeholder="Italian, Mexican, Asian, Mediterranean"
            className="input-field"
          />
          <span className="field-hint">Separate multiple cuisines with commas</span>
        </div>
        
        <div className="form-group">
          <label>Food Dislikes</label>
          <input 
            type="text" 
            value={profileData.foodDislikes?.join(', ') || ''}
            onChange={(e) => setProfileData({
              ...profileData, 
              foodDislikes: e.target.value.split(',').map(s => s.trim())
            })}
            placeholder="Olives, Mushrooms, Cilantro"
            className="input-field"
          />
          <span className="field-hint">We'll avoid these ingredients in your meal plans</span>
        </div>
      </div>

      <div className="profile-section">
        <h3 className="section-title">Household Settings</h3>
        <div className="info-card">
          <p>🏠 Manage your household members, shared preferences, and family meal planning settings.</p>
          <button 
            className="button-secondary"
            onClick={() => window.location.href = '/household'}
          >
            Go to Household Settings
          </button>
        </div>
      </div>

      <div className="action-bar">
        {saveMessage && (
          <div className="save-message">✅ {saveMessage}</div>
        )}
        <button className="button-primary" onClick={saveProfile}>
          Save Preferences
        </button>
      </div>
    </div>
  );

  return (
    <div className="account-page">
      <div className="account-header">
        <div className="header-content">
          <h1 className="account-title">Account Settings</h1>
          <p className="account-subtitle">
            Manage your profile, subscription, and preferences
          </p>
        </div>
        <div className="header-actions">
          <button 
            className="button-icon"
            onClick={() => window.location.href = '/support'}
            title="Get Help"
          >
            🆘
          </button>
        </div>
      </div>

      <div className="account-tabs">
        <button 
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <span className="tab-icon">👤</span>
          Profile
        </button>
        <button 
          className={`tab-button ${activeTab === 'subscription' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscription')}
        >
          <span className="tab-icon">💳</span>
          Subscription
        </button>
        <button 
          className={`tab-button ${activeTab === 'preferences' ? 'active' : ''}`}
          onClick={() => setActiveTab('preferences')}
        >
          <span className="tab-icon">🍽️</span>
          Preferences
        </button>
      </div>

      <div className="account-content">
        {activeTab === 'profile' && renderProfileTab()}
        {activeTab === 'subscription' && renderSubscriptionTab()}
        {activeTab === 'preferences' && renderPreferencesTab()}
      </div>

      <div className="account-footer">
        <div className="footer-links">
          <a href="/privacy" className="footer-link">Privacy Policy</a>
          <a href="/terms" className="footer-link">Terms of Service</a>
          <a href="mailto:support@nutrir.ai" className="footer-link">Contact Support</a>
        </div>
        <div className="danger-zone">
          <button className="button-danger-text">
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default Account;
